/* ===================================================================
   Check registry: the per-spine checks (catalog, participant, clean,
   memo, doc_register, conflict, newpart, anomaly, privilege, phase,
   senior_associate) with their thresholds, prompts, models, and the
   helper functions that meter them.
=================================================================== */
import { FALLBACK_MODEL_PRICES, DEFAULT_MODEL_ID, resolveModelId } from "./constants";
import { estTok, ck } from "./utils";
import type {
  CheckMeta, CheckMap, CheckMapEntry, SimEmail, SimReplay,
} from "./types";

/* The canonical list of checks the spine runs. Order matters — it's the
   pipeline order rendered in the spine. */
export const CHECKS: CheckMeta[] = [
  { id: "catalog", name: "Catalog / Association", tau: 0.70, model: "anthropic/claude-sonnet-4.6", outTok: 40,
    kind: "spine",
    inputs: ["Addresses", "Subject", "Delta", "Working groups", "Thread history"],
    outputs: ["Workstream routing + confidence"],
    desc: "Route each inbound communication to the correct open workstream(s) using participants, subject, identifiers, and thread lineage.",
    threshold_rationale: "A miscatalog is a confidentiality event — the threshold must be conservative. But too high floods the lawyer with routine confirmations. 0.70 balances safety with usability. Auto-file at 0.90 handles the 80% of emails that clearly belong to one matter.",
    prompt: `You are cataloging an inbound legal communication to open matters.

INPUT:
- Sender and recipient addresses: {addresses}
- Subject line: {subject}
- Cleaned message body (delta): {delta}
- Candidate workstreams with their working groups: {working_groups}
- Thread history (prior catalog assignments): {thread_history}

TASK: Determine which workstream(s) this message belongs to.

SIGNALS (weight in order):
1. Working-group membership — sender/recipients match a matter's confirmed participants
2. Matter identifiers — matter codes, entity names, deal names, document names
3. Thread lineage — prior messages in this thread were cataloged to a specific matter
4. Content analysis — substance of the message relates to a specific deal

OUTPUT (JSON):
{
  "catalog": [
    {"workstream_id": "ws_xxx", "confidence": 0.0-1.0, "reason": "brief explanation"}
  ]
}

RULES:
- A message may match ONE, MANY, or NO workstreams.
- Multi-match is expected (e.g., a financing thread also touching a related commercial agreement).
- If no confident match, return an empty catalog array.
- Never assert a new matter into existence.
- If the message has document attachments, factor the document type and content into the routing decision.` },

  { id: "participant", name: "Participant Resolution", tau: 0.80, model: "anthropic/claude-sonnet-4.6", outTok: 60,
    kind: "spine",
    inputs: ["Addresses (from/to/cc)", "Working groups"],
    outputs: ["Address→person matches", "Unknown addresses"],
    desc: "Map each email address to a known working-group person and role, biasing toward abstention over mismatch.",
    threshold_rationale: "Participant matching is high-volume, low-ambiguity. The main failure mode is domain collisions (two counsel firms with similar domains). Bias toward abstaining rather than mismatching.",
    prompt: `Match each email address to a person in the working groups for this matter.

INPUT:
- Addresses to resolve: {addresses} (from, to, cc)
- Working groups by matter: {working_groups} (entity → people with name, email, role)

OUTPUT (JSON):
{
  "matches": [
    {"address": "email@domain", "person_id": "xxx", "entity_id": "xxx", "role": "Partner", "confidence": 0.0-1.0}
  ],
  "unknown": ["unmatched@domain"]
}

RULES:
- Match on exact email first, then name+domain, then domain+role signals.
- Do NOT guess across entities with similar domains (e.g., two law firms).
- Unknown addresses are handed to the new-participant detector.
- Confidence < 0.80 → flag for human confirmation.` },

  { id: "clean", name: "Clean / Delta", tau: 0.60, model: "anthropic/claude-sonnet-4.6", outTok: 140,
    kind: "spine",
    inputs: ["Raw message body"],
    outputs: ["Delta (new content only)", "Attachment references"],
    desc: "Strip quoted history, signatures, and disclaimers; isolate only the new authored content in this message.",
    threshold_rationale: "Over-stripping a first-time forward is the costly error. Err toward keeping content. The threshold is low because the fallback (using the full body) is safe, just noisy.",
    prompt: `Extract only the NEW content authored in THIS message.

INPUT:
- Raw message body with headers: {raw}

TASK: Separate the author's new content from quoted/forwarded material.

REMOVE:
- Quoted prior messages (lines starting with > or preceded by "On [date], [Name] wrote:")
- Email signatures (name, title, firm, phone, email blocks after -- or similar)
- Confidentiality disclaimers and legal notices
- Auto-generated footers, out-of-office notices

PRESERVE:
- Substantive forwarded content being introduced for the FIRST TIME
- Inline commentary on quoted text (the author's additions)
- Attachment references ("please find attached...")

OUTPUT (JSON):
{
  "delta": "the new content only",
  "removed": {
    "quoted_history": true/false,
    "signature": true/false,
    "disclaimer": true/false
  },
  "confidence": 0.0-1.0,
  "attachment_references": ["filename1.docx", "filename2.pdf"]
}` },

  { id: "memo", name: "Deal-Memo Extraction", tau: 0.65, model: "anthropic/claude-sonnet-4.6", outTok: 120,
    kind: "spine",
    inputs: ["Delta", "Attachments", "Deal type", "Current memo"],
    outputs: ["Candidate facts + provenance"],
    desc: "Extract candidate deal-memo facts asserted in the email body or attached documents, with field-level taxonomy and provenance.",
    threshold_rationale: "This is where accuracy matters most. A missed fact is bad; a wrong fact corrupts the memo. Opus is warranted despite cost because the volume is lower (only substantive emails trigger extraction) and the consequences of error are high. Contradictions always elevate regardless of τ.",
    prompt: `Extract deal-memo facts asserted in this communication.

INPUT:
- Cleaned message delta: {delta}
- Document attachments (if any): {attachments} — include relevant sections
- Deal type: {deal_type}
- Current deal memo (confirmed facts with history): {memo}

FIELD TAXONOMY (extract these when asserted):
- deal_type, round_size, pre_money_valuation, post_money_valuation
- security_type, price_per_share, liquidation_preference, participation
- anti_dilution, option_pool_pct, option_pool_shares
- board_composition, protective_provisions, drag_along, tag_along
- dividends, redemption, pay_to_play, founders_vesting
- closing_conditions, exclusivity_period, expense_cap
- key_holder_lockup, information_rights, registration_rights

OUTPUT (JSON):
{
  "facts": [
    {
      "key": "round_size",
      "value": "$45M",
      "source_span": "exact quote from the text where this was stated",
      "source_type": "email_body" | "document_attachment",
      "source_doc": "filename.docx (if from attachment)",
      "source_section": "Section 2.1 (if from document)",
      "confidence": 0.0-1.0,
      "relation": "new" | "consistent_update" | "contradiction"
    }
  ]
}

RULES:
- Only extract terms ACTUALLY STATED — do not infer or calculate.
- For each fact, classify its relation to the current memo:
  - "new" = not in the memo yet
  - "consistent_update" = changes a value in a consistent trajectory (e.g., $40M → $45M from a later authoritative source)
  - "contradiction" = conflicts with a confirmed memo value
- When extracting from document attachments, cite the specific section.
- Numbers must include units ($, %, shares). Capture the exact source span for verification.` },

  { id: "conflict", name: "Fact-Conflict Detection", tau: 0.55, model: "anthropic/claude-sonnet-4.6", outTok: 70,
    kind: "spine",
    inputs: ["Candidate fact", "Memo value + history"],
    outputs: ["Conflict / supersede / restate verdict"],
    desc: "Distinguish a genuine contradiction with the confirmed memo from a legitimate value evolution. Never auto-resolve.",
    threshold_rationale: "This is the hardest judgment in the system. A later conformed document often resolves an earlier conflict. The low threshold is intentional — it's better to surface a possible conflict for human review than to miss a real one. Conflicts NEVER auto-resolve.",
    prompt: `Determine if a candidate fact CONFLICTS with the confirmed deal memo.

INPUT:
- Candidate: {key} = {new_value} (source: {source})
- Current memo: {key} = {current_value} (source: {current_source}, confirmed by: {confirmed_by})
- Fact history: {history} (prior values and sources)

TASK: Is this a genuine conflict (mutually exclusive values) or a legitimate evolution?

CONFLICT = two live values that cannot both be true (e.g., "participating" vs "non-participating")
EVOLUTION = a consistent update trajectory (e.g., round size $40M → $45M from negotiation)

OUTPUT (JSON):
{
  "conflict": true/false,
  "confidence": 0.0-1.0,
  "both_values": [
    {"value": "xxx", "source": "xxx"},
    {"value": "yyy", "source": "yyy"}
  ],
  "rationale": "why this is/isn't a conflict"
}

RULES:
- A later conformed document resolving an earlier discrepancy is NOT a conflict — it's a resolution.
- Numbers with different units or rounding are NOT conflicts unless the delta is material.
- When one value comes from a document and the other from email text, note the provenance difference.
- Conflicts NEVER auto-apply. They always go to the human queue.` },

  { id: "newpart", name: "New-Participant Detection", tau: 0.75, model: "anthropic/claude-sonnet-4.6", outTok: 30,
    kind: "spine", slug: "new-participant-verifier", group: "Guards & monitors",
    inputs: ["Unknown address", "Working group", "Signature block"],
    outputs: ["New-participant proposal (entity, role, side)"],
    desc: "Flag senders or recipients outside the confirmed working group and propose entity, role, and side.",
    threshold_rationale: "Distinguishing a genuine new party from a one-off CC is the key judgment. The verifier agent pre-screens, but every new participant must be confirmed by the lawyer because it affects future cataloging and privilege reasoning.",
    prompt: `Determine if an unknown email address represents a new deal participant.

INPUT:
- Unknown address: {address} ({display_name}, domain: {domain})
- Email context: {subject}, {delta}
- Working group for this matter: {working_group}
- Signature block (if present): {signature}

OUTPUT (JSON):
{
  "is_new_participant": true/false,
  "suggested_entity": "Entity Name or null",
  "suggested_role": "Role description",
  "suggested_side": "company | investor | counsel | service_provider | regulator",
  "confidence": 0.0-1.0,
  "rationale": "why this person appears to be a new participant"
}

RULES:
- Match domain against known entities first.
- A one-off CC (admin, assistant) is NOT a new participant unless they appear substantively.
- Service providers (transfer agents, printers, accountants) ARE new participants.
- Spam and vendor solicitation are NOT new participants.` },

  { id: "anomaly", name: "Off-Phase Anomaly", tau: 0.60, model: "anthropic/claude-sonnet-4.6", outTok: 50,
    kind: "spine",
    inputs: ["Current phase", "Delta", "Tags", "Attachments"],
    outputs: ["Off-phase anomaly flag + kind"],
    desc: "Flag activity inconsistent with the matter's current phase (e.g., a redline arriving Post-Closing).",
    threshold_rationale: "Off-phase activity is how problems surface. A redline arriving in Post-Closing means something is wrong. But phase boundaries are fuzzy (diligence bleeds across phases), so the threshold can't be too aggressive.",
    prompt: `Flag activity inconsistent with the matter's current phase.

INPUT:
- Current phase: {phase} (one of: Setup, Terms, Documentation, Pre-Closing, Post-Closing)
- Email content: {delta}
- Tags from other checks: {tags}
- Document attachments: {attachments}

PHASE-EXPECTED ACTIVITIES:
- Setup: engagement, conflicts, working group, NDA, preliminary structuring
- Terms: term sheet drafts, economic negotiation, key terms, internal approvals
- Documentation: definitive agreements, redlines, diligence, disclosure schedules
- Pre-Closing: conditions precedent, consents, funds flow, signature pages, closing logistics
- Post-Closing: closing binder, post-closing filings, surviving covenants, governance onboarding

OUTPUT (JSON):
{
  "anomaly": true/false,
  "kind": "off-phase" | "regression" | "unexpected_document" | "premature_activity",
  "confidence": 0.0-1.0,
  "rationale": "what activity was detected and why it doesn't fit the phase"
}

RULES:
- Activities that span phases (diligence, disclosure prep) are NOT anomalies.
- A term renegotiation in Pre-Closing IS an anomaly.
- A redline of a definitive agreement in Post-Closing IS an anomaly.
- A new party appearing in Documentation who wasn't in Setup IS an anomaly (hand off to new-participant).` },

  { id: "privilege", name: "Confidentiality / Privilege Guard", tau: 0.70, model: "anthropic/claude-sonnet-4.6", outTok: 50,
    kind: "spine", slug: "confidentiality-privilege", group: "Guards & monitors",
    inputs: ["Recipients", "Internal privilege circle", "Delta"],
    outputs: ["Privilege-risk flag + exposure"],
    desc: "Flag privilege risk — privileged content exposed outside the privileged circle. Flag only; never redact.",
    threshold_rationale: "Privilege flags are serious — a missed waiver has legal consequences. But the system flags, never redacts. The internal-firm entity is the anchor for determining what's privileged. Always requires human review.",
    prompt: `Detect privilege risks in this communication.

INPUT:
- Internal firm circle for this matter: {internal_people} (attorneys and staff)
- All recipients of this message: {recipients}
- Message content: {delta}
- Forwarded content (if any): {forwarded}

PRIVILEGE RISK SCENARIOS:
1. Legal advice or privileged analysis forwarded to a non-privileged party
2. Non-privileged party CC'd on a counsel-only thread
3. Privileged document attached and sent outside the privilege circle
4. Client forwarding attorney advice to opposing counsel or third parties
5. Discussion of legal strategy with non-privileged parties present

OUTPUT (JSON):
{
  "privilege_risk": true/false,
  "kind": "waiver_risk" | "inadvertent_disclosure" | "ethical_wall_breach",
  "exposed_to": ["name/email of non-privileged recipients"],
  "privileged_content": "brief description of what was exposed",
  "confidence": 0.0-1.0
}

RULES:
- The firm's internal team defines the privileged circle.
- Client-to-client communications are NOT privileged unless counsel is involved.
- Flag, never redact. The lawyer decides the response.
- A CEO forwarding attorney advice to an investor is a waiver risk.` },

  { id: "phase", name: "Phase / Milestone Monitor", tau: 0.70, model: "anthropic/claude-sonnet-4.6", outTok: 30,
    kind: "spine", slug: "phase-monitor", group: "Guards & monitors",
    inputs: ["Current phase", "Delta", "Attachments"],
    outputs: ["Milestone + new phase"],
    desc: "Detect milestone events (term sheet signed, signing, closing) that transition the matter to a new phase.",
    threshold_rationale: "Milestones are concrete events with clear signals — Haiku handles this well. But the phase change has downstream effects (it changes how anomaly detection works), so it always needs lawyer confirmation.",
    prompt: `Detect deal milestones that indicate a phase transition.

INPUT:
- Current phase: {phase}
- Email content: {delta}
- Document attachments: {attachments}

MILESTONES (in order):
- Matter Opened → Engaged (conflicts cleared, working group identified, engagement signed)
- Engaged → Term Sheet Signed (principals execute the term sheet/LOI)
- Term Sheet Signed → Signing (definitive agreements executed by all parties)
- Signing → Closing (all conditions satisfied, value changes hands)
- Closing → Matter Closed (all post-closing items complete)

OUTPUT (JSON):
{
  "milestone_detected": true/false,
  "milestone": "Term Sheet Signed" | "Signing" | "Closing" | etc,
  "new_phase": "Documentation" | "Pre-Closing" | etc,
  "confidence": 0.0-1.0,
  "evidence": "what in the communication indicates this milestone"
}

RULES:
- Milestones are bounding events, not activities. "We're drafting the SPA" is NOT a milestone.
- "All signature pages released and documents dated today" IS a milestone (Signing).
- Sign-and-close: Signing and Closing can be the same event.
- Multi-close deals: Closing milestone can repeat.` },

  { id: "doc_register", name: "Document Register", tau: 0.65, model: "anthropic/claude-sonnet-4.6", outTok: 500,
    kind: "spine",
    inputs: ["Attachments", "Deal type", "Memo fields", "Document register", "Lawyer statements"],
    outputs: ["doc_type + bears_on", "noise / substantive / updated_version", "authoritative version verdict (is_current / supersedes)", "authority_conflict → lawyer-in-the-loop"],
    desc: "Owns the document register end-to-end: classifies each attachment's type and the deal terms it bears on, triages it as noise / substantive / updated_version against the running register, and renders an authoritative version verdict (which version is current, what it supersedes). The lawyer's own statements override inferred version logic; when a new version contradicts a prior lawyer assertion, it does NOT auto-decide — it flags an authority_conflict for the lawyer.",
    threshold_rationale: "This is the single owner of the document funnel: type classification, triage, and version authority. Mistyping cascades into wrong routing; a wrong currency verdict can hide the operative draft. Sonnet handles the standard deal-doc taxonomy and version reasoning. Auto-tag clear cases at 0.85; version conflicts against an explicit lawyer assertion never auto-resolve.",
    prompt: `Own the document register: classify, triage, and render an authoritative version verdict for each attachment.

INPUT:
- Email context: {subject}, {delta}
- Attachments: {attachments} (filename, content)
- Deal type: {deal_type}
- Current deal memo fields: {memo_keys}
- Document register (known documents with versions): {document_register}
- Lawyer's relevant statements (authoritative): {lawyer_statements}

DOCUMENT TYPE TAXONOMY:
term_sheet, spa (stock purchase agreement), charter (certificate of incorporation),
ira (investors rights agreement), rofr (right of first refusal / co-sale),
voting_agreement, side_letter, board_consent, cap_table, opinion_letter,
engagement_letter, nda, disclosure_schedule, closing_checklist, executed_package

OUTPUT (JSON):
{
  "documents": [
    {
      "filename": "xxx.docx",
      "doc_type": "charter",
      "bears_on": ["liquidation_preference", "board_composition"],
      "key_sections": ["Section 4(a) - Liquidation"],
      "classification": "noise" | "substantive" | "updated_version",
      "register_match": "register key if updated_version, else null",
      "version": 3,
      "supersedes": 2,
      "is_current": true,
      "authority_source": "inferred" | "lawyer_asserted",
      "authority_conflict": false,
      "authority_conflict_reason": "set only when authority_conflict is true",
      "confidence": 0.0-1.0,
      "rationale": "brief explanation"
    }
  ]
}

RULES:
- doc_type uses the taxonomy above; "bears_on" links to the deal-memo field taxonomy (see Check #4).
- Cap tables route to the attachment slot, never to a memo field.
- classification: "noise" = logos / calendar invites / signature images / marketing;
  "substantive" = a deal document not yet in the register; "updated_version" = a new
  version of a document already in the register (match on doc type + name → register_match).
- Version: infer from filename, email context, or document header. For a redline/markup,
  note the base version it modifies in "supersedes".
- AUTHORITATIVE VERSION VERDICT: set is_current per attachment. By default the highest
  version of a doc_type supersedes lower ones (is_current=true on the newest, supersedes=prior).
- LAWYER AUTHORITY OVERRIDE: if the lawyer's statements assert which version is operative,
  that overrides inferred numbering. Set authority_source="lawyer_asserted" and make
  is_current reflect the asserted version even if a higher-numbered draft exists.
- TEMPORAL CONFLICT → LAWYER-IN-THE-LOOP: if a NEW version contradicts an explicit prior
  lawyer assertion (e.g. lawyer said v3 is final, then v4 arrives), do NOT auto-decide.
  Set authority_conflict=true with authority_conflict_reason explaining the contradiction,
  and leave is_current for the lawyer's determination. Same human-in-the-loop pattern as
  newpart / privilege — never silently overwrite the lawyer's call.
- Updated/substantive documents feed the delta review and enter the register.` },

  { id: "doc_delta", name: "Document Delta Review", tau: 0.70, model: "anthropic/claude-sonnet-4.6", outTok: 800,
    kind: "spine",
    inputs: ["Updated document", "Prior version", "Memo"],
    outputs: ["Material changes + severity"],
    desc: "Compare an updated document to its prior version and report material changes with severity.",
    threshold_rationale: "Material change detection drives senior-associate escalation. High-severity findings must be reliable; minor textual drift must not page anyone. Opus handles the legal-economic judgment required to separate the two.",
    prompt: `Compare the updated document to its prior version and report material changes.

INPUT:
- Updated document: {filename}, {content}
- Prior version (from register): {prior_content}
- Current deal memo: {memo}

OUTPUT (JSON):
{
  "changes": [
    {
      "section": "Section 4(a) - Liquidation",
      "summary": "what changed",
      "severity": "high" | "medium" | "low",
      "bears_on": ["liquidation_preference"],
      "confidence": 0.0-1.0
    }
  ],
  "overall_severity": "high" | "medium" | "low" | "none"
}

RULES:
- Severity is legal-economic materiality, not textual volume. A one-word change to participation rights is high; reformatted recitals are low.
- "bears_on" links changes to deal-memo fields so conflicts can be detected downstream.
- High-severity changes escalate to the Senior Associate review.` },

  { id: "senior_associate", name: "Senior Associate Review", tau: 0.60, model: "anthropic/claude-sonnet-4.6", outTok: 200, external: true,
    kind: "workstream", slug: "senior-associate", group: "Consistency & standards",
    inputs: ["Memo facts", "Document register", "Doc-delta signal", "Cap table"],
    outputs: ["Escalation recommendation + urgency"],
    desc: "External API. Cross-document consistency, key-number reconciliation against the cap table, version currency, and conformity to firm precedent and playbook positions.",
    threshold_rationale: "This check calls the separate Senior Associate service rather than a model. The API returns structured findings with verdicts (PASS/WARN/FAIL). Findings with verdict FAIL or severity Critical/High are always surfaced to the lawyer regardless of τ.",
    prompt: `The Senior Associate reviews cross-document consistency, key numbers against the cap table,
document version currency, and conformity to firm precedent/playbook positions.

This check calls the configured Senior Associate service (POST {SA_BASE}/check) with the
current deal state and returns structured findings.

API PAYLOAD: {memo_facts, document_contents, working_group, cap_table}
API RESPONSE: {checks: [{id, name, verdict, severity, confidence, finding, recommendation}]}

Findings with verdict FAIL or severity Critical/High are surfaced to the lawyer.
If the service is unavailable, pre-baked findings from the simulation data are used.` },

  /* ---- Downstream agent checks (kind: "workstream") — run after the spine,
     consuming its output (routing, facts, doc triage/delta, flags). ---- */

  { id: "dispatch", name: "Document Dispatch", tau: 0.60, model: "anthropic/claude-sonnet-4.6", outTok: 60,
    kind: "workstream", slug: "document-dispatch", group: "Work production",
    trigger: "Routed, with attachments or document-action language",
    inputs: ["Delta", "Spine summary", "Document register", "Memo"],
    outputs: ["Document actions (draft / revise / circulate / execute / file)"],
    desc: "Reads communications and decides which documents need action — draft, revise, circulate, execute, or file — and by whom.",
    threshold_rationale: "Dispatching phantom work wastes the team's time; missing a requested revision delays the deal. Mid threshold with explicit-request bias keeps both in check.",
    prompt: `ROLE: You are the document-dispatch agent on a deal team.
TASK: From this communication and the spine output, decide which documents need action (draft, revise, circulate, execute, file) and by whom.

INPUT:
- Cleaned message delta: {delta}
- Spine output: {spine_summary} (routing, extracted facts, doc triage, flags)
- Document register: {document_register}
- Current deal memo: {memo}

OUTPUT (JSON):
{
  "actions": [
    {"document": "SPA", "action": "revise | draft | circulate | execute | file", "owner": "company counsel", "reason": "why"}
  ],
  "conf": 0.0-1.0,
  "out": "N document actions identified."
}

RULES:
- Only dispatch on explicit or clearly implied requests; do not invent work.
- Tie each action to the document register entry when one exists.
- Empty actions array is valid when nothing requires document work.` },

  { id: "docchange", name: "Document Change", tau: 0.60, model: "anthropic/claude-sonnet-4.6", outTok: 80,
    kind: "workstream", slug: "document-change", group: "Work production",
    trigger: "Dispatch proposed document actions",
    inputs: ["Dispatch actions", "Delta", "Memo", "Document register"],
    outputs: ["Concrete edits (section, current language, required change)"],
    desc: "Specifies the concrete edits the dispatched document work requires — section, current language, required change.",
    threshold_rationale: "Edits must trace to a stated basis. Runs only after dispatch proposes actions, so volume is low; precision of the cited section matters more than recall.",
    prompt: `ROLE: You are the document-change agent; you specify the edits the dispatched document work requires.
TASK: For each document needing revision, specify the concrete edits (section, current language, required change).

INPUT:
- Cleaned message delta: {delta}
- Spine output incl. dispatched actions: {spine_summary}
- Current deal memo: {memo}
- Document register: {document_register}

OUTPUT (JSON):
{
  "edits": [
    {"document": "Charter", "section": "4(a) Liquidation", "change": "what must change", "basis": "email/memo source"}
  ],
  "conf": 0.0-1.0,
  "out": "N edits specified."
}

RULES:
- Edits must trace to something stated in the communication or memo; cite the basis.
- Specify the section when inferable; never fabricate section numbers.
- No edits required → empty array.` },

  { id: "timeline", name: "Timeline / PM Tracker", tau: 0.60, model: "anthropic/claude-sonnet-4.6", outTok: 40,
    kind: "workstream", slug: "timeline", group: "Trackers",
    trigger: "Routed and the cleaned body is non-empty",
    inputs: ["Delta", "Spine summary", "Email date"],
    outputs: ["Timeline entries (milestones, deadlines, events)"],
    desc: "Maintains the matter's universal timeline and critical dates from dated events and deadlines asserted in communications.",
    threshold_rationale: "Dates are concrete signals Haiku extracts reliably. A spurious entry is cheap to ignore; a missed closing deadline is not — bias toward recording.",
    prompt: `ROLE: You maintain the matter's universal timeline and critical dates.
TASK: Extract dated events and deadlines asserted in this communication for the matter timeline.

INPUT:
- Cleaned message delta: {delta}
- Email date: {date}
- Spine output: {spine_summary}

OUTPUT (JSON):
{
  "entries": [{"date": "2026-04-10", "event": "Term sheet signed", "kind": "milestone | deadline | event", "status": "pending | done | changed | cancelled"}],
  "conf": 0.0-1.0,
  "out": "N timeline entries."
}

RULES:
- Only record dates actually stated or directly computable ("by Friday" + email date).
- Record substantive meetings, deadlines, expected deliveries, signatures or execution, closings, filings, payments, effective or expiry dates, and dated personnel handoffs.
- Keep each event to one short factual sentence. Exclude routine email sent dates, signatures, disclaimers, and quoted repetitions.
- Use status "pending" for future or outstanding events, "done" for completed events, and "changed" or "cancelled" only when the communication says so.
- Normalize dates to ISO where possible.` },

  { id: "issues", name: "Open-Issues List", tau: 0.60, model: "anthropic/claude-sonnet-4.6", outTok: 40,
    kind: "workstream", slug: "open-issues", group: "Trackers",
    trigger: "Routed and the cleaned body is non-empty",
    inputs: ["Delta", "Spine summary", "Thread"],
    outputs: ["Issues raised / resolved / escalated"],
    desc: "Tracks the open issues as actually communicated — raised, resolved, or escalated between the parties.",
    threshold_rationale: "Issues are explicit disagreements, not tasks. Resolution requires asserted closure, so false 'resolved' states are the costly error.",
    prompt: `ROLE: You track the matter's open-issues list as actually communicated.
TASK: Identify issues raised, resolved, or still open in this communication.

INPUT:
- Cleaned message delta: {delta}
- Spine output: {spine_summary}
- Thread history: {thread_history}

OUTPUT (JSON):
{
  "issues": [{"issue": "Board size disagreement", "status": "open | resolved | escalated", "owner": "Ridgeline"}],
  "conf": 0.0-1.0,
  "out": "N issues tracked."
}

RULES:
- An issue is a point of disagreement or unresolved question between parties, not a task.
- Mark resolved only when the communication states resolution.
- Attribute the owner (who must act) when stated.` },

  { id: "closing", name: "Closing Checklist", tau: 0.60, model: "anthropic/claude-sonnet-4.6", outTok: 60,
    kind: "workstream", slug: "closing-checklist", group: "Trackers",
    trigger: "Phase transition, milestone, or closing-context language",
    inputs: ["Delta", "Spine summary", "Memo"],
    outputs: ["Closing-condition status changes (open / satisfied / waived)"],
    desc: "Tracks the substantive conditions to close — open, satisfied, or waived — with the evidence for each status change.",
    threshold_rationale: "Marking a condition satisfied on intention rather than completion is the dangerous error at closing. Status changes require asserted evidence.",
    prompt: `ROLE: You track the substantive conditions to close.
TASK: Update the closing checklist from this communication: conditions opened, satisfied, or waived.

INPUT:
- Cleaned message delta: {delta}
- Spine output: {spine_summary}
- Current deal memo: {memo}

OUTPUT (JSON):
{
  "items": [{"condition": "Stockholder consent", "status": "open | satisfied | waived", "evidence": "what was said"}],
  "conf": 0.0-1.0,
  "out": "N closing items updated."
}

RULES:
- Conditions are substantive (consents, opinions, certificates, funds), not logistics.
- "satisfied" requires asserted completion, not intention.
- Cite the evidence phrase for each status change.` },

  { id: "signature", name: "Signature & Execution", tau: 0.60, model: "anthropic/claude-sonnet-4.6", outTok: 40,
    kind: "workstream", slug: "signature-execution", group: "Trackers",
    trigger: "Signature or execution language (body or attachments)",
    inputs: ["Delta", "Spine summary", "Document register"],
    outputs: ["Signature / execution status per document and party"],
    desc: "Tracks who has signed what and what's still outstanding to execute, including released-into-escrow states.",
    threshold_rationale: "Execution events are concrete and keyword-anchored. The released/signed distinction matters for closing mechanics, so only asserted events are recorded.",
    prompt: `ROLE: You track signature and execution status across the deal documents.
TASK: From this communication, record who has signed what and what remains outstanding.

INPUT:
- Cleaned message delta: {delta}
- Spine output: {spine_summary}
- Document register: {document_register}

OUTPUT (JSON):
{
  "signatures": [{"document": "SPA", "party": "Lumen Robotics", "status": "signed | released | outstanding"}],
  "conf": 0.0-1.0,
  "out": "N signature updates."
}

RULES:
- "released" = signature pages released into escrow pending closing.
- Only record execution events asserted in the communication.
- Note countersignature requirements when stated.` },

  { id: "diligence", name: "Diligence Tracker", tau: 0.60, model: "anthropic/claude-sonnet-4.6", outTok: 40,
    kind: "workstream", slug: "diligence", group: "Trackers",
    trigger: "Diligence or data-room language",
    inputs: ["Delta", "Spine summary"],
    outputs: ["Diligence items (requested / provided / outstanding)"],
    desc: "Maintains the diligence request list — requested, provided, and outstanding items at request-list granularity.",
    threshold_rationale: "Diligence flow is high-volume and mechanical; Haiku suffices. Tracking granularity stays at the request list, not individual files.",
    prompt: `ROLE: You maintain the diligence request list.
TASK: Track diligence items requested, provided, or still outstanding per this communication.

INPUT:
- Cleaned message delta: {delta}
- Spine output: {spine_summary}

OUTPUT (JSON):
{
  "requests": [{"item": "Customer contracts > $100k", "status": "requested | provided | outstanding", "party": "company"}],
  "conf": 0.0-1.0,
  "out": "N diligence items."
}

RULES:
- Data-room uploads and document deliveries count as "provided".
- Keep items at request-list granularity, not individual files.
- Only track diligence flow, not negotiation substance.` },

  { id: "commitments", name: "Commitments & Waiting-On", tau: 0.60, model: "anthropic/claude-sonnet-4.6", outTok: 40,
    kind: "workstream", slug: "commitments", group: "Trackers",
    trigger: "Routed and the cleaned body is non-empty",
    inputs: ["Delta", "Spine summary", "Email date"],
    outputs: ["Commitments (who / what / when / direction)"],
    desc: "Tracks forward asks and dependencies — who promised what to whom by when, and what this side is waiting on.",
    threshold_rationale: "A commitment needs an actor and a deliverable; vague intentions are noise. Open loops are cheap to track and expensive to drop.",
    prompt: `ROLE: You track forward commitments and waiting-on dependencies (open loops).
TASK: Extract who promised what to whom by when, and what this side is waiting on.

INPUT:
- Cleaned message delta: {delta}
- Email date: {date}
- Spine output: {spine_summary}

OUTPUT (JSON):
{
  "commitments": [{"who": "David Okafor", "what": "send updated cap table", "due": "2026-05-04", "direction": "owed_by_them | owed_by_us"}],
  "conf": 0.0-1.0,
  "out": "N commitments tracked."
}

RULES:
- A commitment needs an actor and a deliverable; vague intentions don't count.
- Capture the due date when stated; null otherwise.
- direction reflects whether our side owes it or is waiting on it.` },

  { id: "fundsflow", name: "Funds-Flow & Wire Integrity", tau: 0.65, model: "anthropic/claude-sonnet-4.6", outTok: 60,
    kind: "workstream", slug: "funds-flow", group: "Trackers",
    trigger: "Wire or funds-flow language (body or attachments)",
    inputs: ["Delta", "Spine summary", "Memo economics"],
    outputs: ["Wires (amount / parties / status)", "Discrepancies"],
    desc: "Tracks funds flow and wire instructions; flags any discrepancy against earlier instructions or deal economics.",
    threshold_rationale: "Wire-instruction changes are the classic fraud vector. Any deviation from previously communicated details is flagged — discrepancies always surface for human review.",
    prompt: `ROLE: You track funds flow and wire integrity for the closing.
TASK: Record wire instructions, amounts, and funds-flow steps; flag discrepancies against known deal economics or earlier instructions.

INPUT:
- Cleaned message delta: {delta}
- Spine output: {spine_summary}
- Current deal memo (economics): {memo}

OUTPUT (JSON):
{
  "wires": [{"amount": "$45,000,000", "from": "Ridgeline Ventures", "to": "Lumen Robotics", "status": "instructed | confirmed | received"}],
  "discrepancies": [{"kind": "amount_mismatch | account_change | unverified_instruction", "detail": "what doesn't reconcile"}],
  "conf": 0.0-1.0,
  "out": "N wires; M discrepancies."
}

RULES:
- ANY change to previously communicated wire instructions is a discrepancy (fraud vector).
- Reconcile amounts against memo economics (round size, fees) when available.
- Never normalize away differences in account or routing details.` },

  { id: "fidelity", name: "Deal-Memo / Document Fidelity", tau: 0.60, model: "anthropic/claude-sonnet-4.6", outTok: 80,
    kind: "workstream", slug: "deal-memo-fidelity", group: "Consistency & standards",
    trigger: "Attachments present + populated memo",
    inputs: ["Attachments", "Memo fields", "Spine summary"],
    outputs: ["Field-level mismatches with cited document language"],
    desc: "Confirms the documents reflect the agreed deal-memo terms; reports field-level mismatches with cited document language.",
    threshold_rationale: "Same stakes as memo extraction: a missed divergence between the documents and the agreed terms corrupts the deal record. Opus-class judgment is warranted on the low-volume doc-bearing emails.",
    prompt: `ROLE: You verify deal-memo/document fidelity — that the documents reflect the agreed memo terms.
TASK: Compare the document content in scope against the confirmed deal-memo fields; report mismatches.

INPUT:
- Document attachments: {attachments}
- Confirmed deal-memo fields: {memo}
- Spine output: {spine_summary}

OUTPUT (JSON):
{
  "mismatches": [
    {"field": "Liquidation preference", "memoValue": "1x non-participating", "docValue": "1x participating", "document": "Charter v2"}
  ],
  "conf": 0.0-1.0,
  "out": "N fidelity mismatches."
}

RULES:
- Only compare fields the memo actually has; absence in a document is not a mismatch unless the term belongs there.
- Quote the document language driving each mismatch.
- Formatting/rounding differences are not mismatches unless material.` },

  { id: "discrepancy", name: "Deal-Memo / Communications Discrepancy", tau: 0.60, model: "anthropic/claude-sonnet-4.6", outTok: 60,
    kind: "workstream", slug: "deal-memo-discrepancy", group: "Consistency & standards",
    trigger: "New facts extracted against populated memo",
    inputs: ["Delta", "Memo values", "Newly extracted facts"],
    outputs: ["Stated-value discrepancies + severity"],
    desc: "Catches contradictions between what is being communicated and the confirmed deal memo, with severity.",
    threshold_rationale: "Supersessions already handled by the spine's conflict check are not discrepancies — this agent watches live communications drift against confirmed values.",
    prompt: `ROLE: You catch contradictions between what is being communicated and the established deal memo.
TASK: Compare assertions in this communication against confirmed memo values; report discrepancies.

INPUT:
- Cleaned message delta: {delta}
- Confirmed memo values: {memo}
- Spine output incl. newly extracted facts: {spine_summary}

OUTPUT (JSON):
{
  "discrepancies": [
    {"field": "Round size", "memoValue": "$45M", "statedValue": "$40M", "speaker": "amorgan@ridgeline.vc", "severity": "high | medium | low"}
  ],
  "conf": 0.0-1.0,
  "out": "N communications discrepancies."
}

RULES:
- A discrepancy is a stated value that disagrees with a confirmed memo value.
- Severity reflects economic/legal materiality of the divergence.
- Consistent updates already captured by the spine (supersessions) are NOT discrepancies.` },

  { id: "stateofplay", name: "State of Play", tau: 0.60, model: "anthropic/claude-sonnet-4.6", outTok: 200,
    kind: "workstream", slug: "state-of-play", group: "Synthesis",
    trigger: "Routed and the cleaned body is non-empty",
    inputs: ["Spine summary", "Memo", "Document register", "Thread", "Tracker states"],
    outputs: ["Living matter dashboard (stage, momentum, recent activity, tracking, next actions)"],
    desc: "Maintains the living state-of-play dashboard — current stage, momentum, recent activity, tracking against expectations, and the next action — refreshed on every routed email.",
    threshold_rationale: "A standing summary is only useful if it is always current, so it refreshes on every routed email rather than waiting for milestones. Everything in the dashboard must ground in the memo, register, trackers, or thread.",
    prompt: `ROLE: You maintain the living state-of-play dashboard for this matter.
TASK: Produce the standing summary a lawyer reads to get oriented on a matter they have not looked at in a few days: where the deal stands right now, not just what this email changed.

INPUT:
- Spine output: {spine_summary}
- Current deal memo: {memo}
- Document register: {document_register}
- Thread history: {thread_history}
- Accumulated tracker state: {tracker_timeline} {tracker_issues} {tracker_closing} {tracker_commitments} {tracker_wires}

OUTPUT (JSON):
{
  "phase": "current stage in the deal lifecycle",
  "momentum": "positive | steady | stalled | concerning",
  "momentumNote": "One sentence on the tone of progress.",
  "briefing": "2-4 sentence orientation: where the deal stands right now.",
  "recentActivity": ["what happened in the last few communications, most recent first"],
  "tracking": "on_track | at_risk | behind",
  "trackingNote": "One sentence on pace against the timeline and stated expectations.",
  "nextActions": [{"action": "what must happen next", "who": "owner"}],
  "conf": 0.0-1.0,
  "out": "One-line state of play."
}

RULES:
- Cover all five elements: current stage, tone of progress (momentum), recent activity, tracking against timeline/expectations, and the next action with its owner.
- Ground every statement in the memo, register, thread history, trackers, or this communication; no speculation.
- Judge momentum from the substance: parties converging and milestones landing is positive; unanswered asks, slipping dates, or escalating disputes are stalled/concerning.
- Judge tracking against the accumulated timeline and any stated deadlines or closing targets; say what is late or at risk, not just that something is.
- Write for a reader with 30 seconds who has been away from the matter for days.` },
];

export const CHECK: Record<string, CheckMeta> = Object.fromEntries(
  CHECKS.map((c) => [c.id, c]),
);
export const CHECK_IDS: string[] = CHECKS.map((c) => c.id);
export const SPINE_CHECK_IDS: string[] = CHECKS.filter((c) => c.kind !== "workstream").map((c) => c.id);
export const DETECTORS: Set<string> = new Set([
  "conflict", "anomaly", "newpart", "privilege", "phase",
]);

/* The Workstream Agent roster: all and only the kind:"workstream" checks,
   grouped for display. Spine checks never appear here. */
export const AGENT_GROUPS: Array<[string, string[]]> = [
  ["Work production", ["dispatch", "docchange"]],
  ["Trackers", ["timeline", "issues", "closing", "signature", "diligence", "commitments", "fundsflow"]],
  ["Consistency & standards", ["senior_associate", "fidelity", "discrepancy"]],
  ["Synthesis", ["stateofplay"]],
];
export const AGENT_CHECK_IDS: string[] = AGENT_GROUPS.flatMap(([, ids]) => ids);

/* Pre-unification seed replays used "numbers" for the Senior Associate agent. */
export const LEGACY_AGENT_ALIAS: Record<string, string> = { numbers: "senior_associate" };

/* Normalize a stored CheckMap at read time: stale snapshots/drafts may carry
   retired tier ids (m_small/m_std/…) or the removed modelOverride field. A
   prior modelOverride was already a concrete slug, so it wins. */
export function normalizeCheckMap(map: CheckMap): CheckMap {
  const out: CheckMap = {};
  for (const [id, entry] of Object.entries(map)) {
    const { modelOverride, ...rest } = entry as CheckMapEntry & { modelOverride?: string };
    out[id] = { ...rest, model: modelOverride || resolveModelId(entry.model) };
  }
  return out;
}

/* Default check map (id → entry with name/desc/prompt/tau/model/outTok/external/rationale). */
export const BASE_CHECK_MAP: CheckMap = Object.fromEntries(
  CHECKS.map((c) => [
    c.id,
    {
      name: c.name,
      desc: c.desc,
      prompt: c.prompt,
      tau: c.tau,
      model: c.model,
      outTok: c.outTok,
      external: !!c.external,
      threshold_rationale: c.threshold_rationale || "",
      kind: c.kind || "spine",
      ...(c.slug && { slug: c.slug }),
      ...(c.group && { group: c.group }),
      ...(c.trigger && { trigger: c.trigger }),
      ...(c.inputs && { inputs: c.inputs }),
      ...(c.outputs && { outputs: c.outputs }),
    } as CheckMapEntry,
  ]),
);

/* Synth a default replay result for an email that has none. */
export function synthResult(email: SimEmail): SimReplay {
  const first =
    (email.raw || "")
      .split("\n")
      .map((s) => s.trim())
      .filter(Boolean)[0] || email.subject;
  return {
    catalogs: [["unassoc", 0.30]],
    delta: first.slice(0, 170),
    facts: [],
    tags: [["log"]],
    checks: [
      ck("catalog", 0.30, "No confident workstream match → Unassociated feed."),
      ck("clean", 0.85, "Delta extracted from new email."),
    ],
    approvals: [],
    actions: [],
  };
}

/* Meter the cost/tokens of running a single check on an email. */
export interface MeteredCheck {
  inTok: number;
  outTok: number;
  cost: number;
  model: string;
  calls?: number;
  latency?: string | number;
}
export function meterCheck(
  meta: CheckMapEntry | CheckMeta | undefined | null,
  email: SimEmail,
): MeteredCheck {
  const isExt = !!(meta && (meta as any).external);
  const attTok = (email.attachments || []).reduce(
    (s, a) => s + estTok(a.content || ""),
    0,
  );
  const ctx = estTok(email.raw || email.subject || "") + attTok;
  const inTok = ctx + estTok((meta && meta.prompt) || "");
  const outTok = (meta && meta.outTok) || 40;
  const model = resolveModelId(meta && meta.model);
  const m = FALLBACK_MODEL_PRICES[model] || FALLBACK_MODEL_PRICES[DEFAULT_MODEL_ID];
  const cost = (inTok / 1e6) * m.inP + (outTok / 1e6) * m.outP;
  const out: MeteredCheck = {
    inTok,
    outTok,
    cost,
    model,
  };
  if (isExt) {
    out.calls = 1;
    out.latency = "~" + (180 + Math.round(Math.random() * 240)) + "ms";
  }
  return out;
}
